
import React, { useState } from 'react';
import { 
  Table, 
  TableBody
} from '@/components/ui/table';
import TableFilters from './TableFilters';
import { useVoltageRegulatorFilters } from './hooks/useVoltageRegulatorFilters';
import VoltageRegulatorTableHeader from './table/VoltageRegulatorTableHeader';
import VoltageRegulatorTableRow from './table/VoltageRegulatorTableRow';
import VoltageRegulatorNoDataRow from './table/VoltageRegulatorNoDataRow';

interface VoltageRegulator {
  deviceId: string;
  location: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  lastCommunication: string;
  firmwareVersion: string;
  readings: {
    inputVoltage: number;
    outputVoltage: number;
    current: number;
    frequency: number;
    regulationAccuracy: number;
    responseTime: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

interface VoltageRegulatorTableProps {
  regulators: VoltageRegulator[];
  onSelectDevice: (deviceId: string) => void;
}

// Helper function to convert filterCategories array to the new Record format
const convertFilterCategoriesToRecord = (categories: { name: string; options: { value: string; label: string; count?: number }[] }[]): Record<string, { label: string; value: string; count?: number }[]> => {
  const result: Record<string, { label: string; value: string; count?: number }[]> = {};
  
  categories.forEach(category => {
    result[category.name] = category.options.map(option => ({
      label: option.label,
      value: option.value,
      count: option.count
    }));
  });
  
  return result;
};

const VoltageRegulatorTable: React.FC<VoltageRegulatorTableProps> = ({ 
  regulators,
  onSelectDevice 
}) => {
  // State for active filters
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    Location: [],
    Status: [],
    BatteryLevel: [],
    SignalStrength: [],
    Regulation: []
  });

  // Use the custom hook to get filter categories and filtered regulators
  const { filterCategories, filteredRegulators } = useVoltageRegulatorFilters(regulators, activeFilters);

  // Handle filter changes
  const handleFilterChange = (category: string, value: string) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      
      if (newFilters[category].includes(value)) {
        // Remove the filter if it's already active
        newFilters[category] = newFilters[category].filter(v => v !== value);
      } else {
        // Add the filter
        newFilters[category] = [...newFilters[category], value];
      }
      
      return newFilters;
    });
  };

  // Clear all filters
  const handleClearFilters = () => {
    setActiveFilters({
      Location: [],
      Status: [],
      BatteryLevel: [],
      SignalStrength: [],
      Regulation: []
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <TableFilters 
          categories={convertFilterCategoriesToRecord(filterCategories)}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
        {Object.values(activeFilters).some(filters => filters.length > 0) && (
          <div className="text-sm text-muted-foreground">
            Showing {filteredRegulators.length} of {regulators.length} regulators
          </div>
        )}
      </div>
      
      <div className="rounded-md border overflow-auto flex-grow h-[calc(100vh-30rem)]">
        <Table>
          <VoltageRegulatorTableHeader />
          <TableBody>
            {filteredRegulators.map((regulator) => (
              <VoltageRegulatorTableRow 
                key={regulator.deviceId}
                regulator={regulator}
                onSelectDevice={onSelectDevice}
              />
            ))}
            {filteredRegulators.length === 0 && (
              <VoltageRegulatorNoDataRow isEmpty={regulators.length === 0} />
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default VoltageRegulatorTable;
